import React, { useEffect, useMemo, useRef, useState } from "react";
import Cookies from "js-cookie";
import { toast } from "react-hot-toast";
import ConfigControls from "./ConfigControls";
import TemplatePreview from "./TemplatePreview";
import {
  DEFAULT_TEMPLATE_CONFIG,
  listTemplates,
  getTemplate,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  setDefaultTemplate,
} from "../../../services/templatesApi";

// Settings > My Organization > Templates > Proposal Templates.
// Split-pane editor matching the Figma: template list (left), live document
// preview (center), tabbed Cover/Body config (right). Org-scoped via
// selected_org_id (handled in templatesApi).

const deepSet = (obj, path, value) => {
  const keys = path.split(".");
  const next = structuredClone(obj);
  let cur = next;
  for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
  cur[keys[keys.length - 1]] = value;
  return next;
};

const CreateModal = ({ onClose, onCreate, saving }) => {
  const [name, setName] = useState("");
  const ref = useRef(null);
  useEffect(() => ref.current?.focus(), []);
  const trimmed = name.trim();
  return (
    <div className="fixed inset-0 z-[10000] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-[16px] shadow-xl w-full max-w-[440px] p-6 font-outfit" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-[20px] font-semibold text-[#27272A]">Create Template</h2>
        <p className="text-[14px] text-[#71717A] mt-1">Name your proposal template.</p>
        <input
          ref={ref}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && trimmed && onCreate(trimmed)}
          placeholder="e.g. Corporate Red"
          className="w-full px-4 py-2.5 rounded-[8px] border border-[#E4E4E7] text-[15px] mt-4 focus:outline-none focus:border-[#BB42AE] focus:ring-1 focus:ring-[#BB42AE]"
        />
        <div className="flex justify-center gap-3 mt-6">
          <button onClick={onClose} className="px-7 py-2.5 rounded-[8px] border border-[#BB42AE] text-[#BB42AE] text-[14px] font-medium hover:bg-[#FBF0F9]">Cancel</button>
          <button
            disabled={!trimmed || saving}
            onClick={() => onCreate(trimmed)}
            className={`px-7 py-2.5 rounded-[8px] text-white text-[14px] font-medium ${trimmed && !saving ? "bg-[#BB42AE] hover:bg-[#A3398D]" : "bg-[#9A9A9A] cursor-not-allowed"}`}
          >
            {saving ? "Creating…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
};

const DeleteTemplateModal = ({ name, onClose, onConfirm, deleting }) => (
  <div className="fixed inset-0 z-[10000] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-white rounded-[16px] shadow-xl w-full max-w-[440px] p-6 font-outfit text-center" onClick={(e) => e.stopPropagation()}>
      <h2 className="text-[18px] font-semibold text-[#27272A]">Delete “{name}”?</h2>
      <p className="text-[14px] text-[#71717A] mt-1.5">This template will be permanently removed. This can't be undone.</p>
      <div className="flex justify-center gap-3 mt-6">
        <button onClick={onClose} className="px-7 py-2.5 rounded-[8px] border border-[#E4E4E7] text-[#3F3F46] text-[14px] font-medium hover:bg-[#F4F4F5]">Cancel</button>
        <button
          disabled={deleting}
          onClick={onConfirm}
          className={`px-7 py-2.5 rounded-[8px] text-white text-[14px] font-medium ${deleting ? "bg-[#E4A0A0] cursor-not-allowed" : "bg-[#DC2626] hover:bg-[#B91C1C]"}`}
        >
          {deleting ? "Deleting…" : "Delete"}
        </button>
      </div>
    </div>
  </div>
);

const ProposalTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  const [draft, setDraft] = useState(null);    // working config being edited
  const [baseline, setBaseline] = useState(null); // last-saved config (for dirty/cancel)
  const [tab, setTab] = useState("cover");      // "cover" | "body"
  // Index of the cover line being edited. Shared by the panel and the canvas so
  // focusing a row highlights it on the page and vice versa.
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [loadingDraft, setLoadingDraft] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // template pending deletion
  const [deleting, setDeleting] = useState(false);

  const orgId = Cookies.get("selected_org_id");

  const load = async () => {
    if (!orgId) {
      setLoading(false);
      toast.error("No workspace selected");
      return;
    }
    setLoading(true);
    try {
      const list = await listTemplates();
      setTemplates(list);
      // Open the default one, so the highlighted row is always the one that
      // exports — landing on a non-default template would make the highlight lie.
      if (list.length && selectedId == null) {
        selectTemplate((list.find((t) => t.is_default) || list[0]).id);
      }
    } catch (e) {
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const selectTemplate = async (id) => {
    setSelectedId(id);
    setSelectedBlock(null);   // indexes belong to the outgoing template
    setLoadingDraft(true);
    try {
      const t = await getTemplate(id);
      setDraft(t.config);
      setBaseline(t.config);
    } catch (e) {
      toast.error("Failed to open template");
    } finally {
      setLoadingDraft(false);
    }
  };

  const dirty = useMemo(
    () => draft && baseline && JSON.stringify(draft) !== JSON.stringify(baseline),
    [draft, baseline]
  );

  const onChange = (path, value) => setDraft((d) => deepSet(d, path, value));

  // Auto-save: whenever the draft diverges from the last-saved config, persist it
  // after a short debounce. No explicit Save button — edits save on their own.
  useEffect(() => {
    if (!selectedId || !dirty) return;
    const timer = setTimeout(async () => {
      setSaving(true);
      const snapshot = draft; // the exact config being persisted
      try {
        await updateTemplate(selectedId, { config: snapshot });
        // Baseline = the snapshot we sent (not the response), so a normalized
        // response can't re-trigger dirty and loop. Newer in-flight edits to
        // `draft` stay dirty and get their own debounced save.
        setBaseline(snapshot);
      } catch (e) {
        toast.error(e?.response?.data?.error || "Failed to save changes");
      } finally {
        setSaving(false);
      }
    }, 700);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, selectedId, dirty]);

  const handleCreate = async (name) => {
    setCreating(true);
    try {
      const t = await createTemplate({ name, config: DEFAULT_TEMPLATE_CONFIG });
      setTemplates((prev) => [...prev, { id: t.id, name: t.name, is_default: t.is_default, is_system: t.is_system }]);
      setShowCreate(false);
      selectTemplate(t.id);
      toast.success("Template created");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Failed to create");
    } finally {
      setCreating(false);
    }
  };

  // Clicking a template in the list opens it AND makes it the one exports use.
  // Deliberately separate from `selectTemplate`, which stays a pure "open" so
  // programmatic selection (initial load, post-delete) can never change the
  // workspace default as a side effect of just visiting this page.
  const openTemplate = (t) => {
    selectTemplate(t.id);
    if (!t.is_default) handleSetDefault(t.id);
  };

  const handleSetDefault = async (id) => {
    // Now a one-click control in the list, so move the dot immediately and roll
    // back if the server rejects it — waiting on the round-trip made the radio
    // feel unresponsive.
    const previous = templates;
    setTemplates((prev) => prev.map((t) => ({ ...t, is_default: t.id === id })));
    try {
      await setDefaultTemplate(id);
      toast.success("Default template set");
    } catch (e) {
      setTemplates(previous);
      toast.error(e?.response?.data?.error || "Failed to set default");
    }
  };

  const handleDelete = async () => {
    const id = confirmDelete?.id;
    if (!id) return;
    setDeleting(true);
    try {
      await deleteTemplate(id);
      const remaining = templates.filter((t) => t.id !== id);
      setTemplates(remaining);
      if (selectedId === id) {
        if (remaining.length) selectTemplate(remaining[0].id);
        else { setSelectedId(null); setDraft(null); setBaseline(null); }
      }
      // Deleting the default would leave the workspace with none, which silently
      // reverts every export to the built-in look. Promote the next one instead.
      if (confirmDelete?.is_default && remaining.length) {
        handleSetDefault(remaining[0].id);
      }
      setConfirmDelete(null);
      toast.success("Template deleted");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="font-outfit w-full h-full flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#E4E4E7] border-t-[#BB42AE] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="font-outfit w-full h-full flex flex-col min-h-0">
      <h1 className="text-[18px] font-semibold text-[#09090B]">Templates</h1>
      <p className="text-[14px] text-[#71717A] mb-4 border-b pb-3 border-[#E4E4E7]">
        Create, customize, and manage templates to standardize workflows across your organization.
      </p>

      <div className="flex-1 min-h-0 flex gap-4">
        {/* LEFT: template list */}
        <div className="w-[210px] shrink-0 flex flex-col border border-[#E4E4E7] rounded-[12px] overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-[#E4E4E7]">
            <span className="text-[14px] font-semibold text-[#18181B]">All Templates</span>
            <button onClick={() => setShowCreate(true)} className="w-6 h-6 rounded-[6px] hover:bg-[#F4F4F5] flex items-center justify-center text-[#52525B] text-[18px] leading-none">+</button>
          </div>
          <div className="flex-1 overflow-y-auto custom-scrollbar py-2">
            <div className="text-[11px] font-semibold text-[#A1A1AA] uppercase tracking-wide px-3 mb-1">Default</div>
            {templates.map((t) => (
              <div
                key={t.id}
                onClick={() => openTemplate(t)}
                className={`group flex items-center gap-1.5 px-3 py-2.5 mx-1 rounded-[8px] cursor-pointer ${selectedId === t.id ? "bg-[#BB42AE]/[0.08] text-[#BB42AE]" : "text-[#3F3F46] hover:bg-[#FAFAFA]"}`}
              >
                <span className="text-[14px] truncate min-w-0 flex-1">{t.name}</span>

                {/* Opening a template is what makes it the export default, so the
                    badge always sits on the open row — it's there to say what the
                    highlight MEANS, not to mark a separate state. */}
                {t.is_default && (
                  <span className="shrink-0 text-[10px] font-semibold text-[#2DAD4E] bg-[#2DAD4E]/[0.12] px-1.5 py-0.5 rounded-full uppercase tracking-wide">
                    Default
                  </span>
                )}

                {/* Seeded presets can't be deleted, so they get no trash at all.
                    focus:opacity-100 keeps it reachable by keyboard, since
                    hover-only controls are otherwise invisible to tab users. */}
                {!t.is_system && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setConfirmDelete(t); }}
                    title={`Delete “${t.name}”`}
                    className="shrink-0 w-7 h-7 rounded-[6px] flex items-center justify-center text-[#DC2626] opacity-0 group-hover:opacity-100 focus:opacity-100 hover:bg-[#FEF2F2] transition-colors"
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
            {templates.length === 0 && (
              <div className="px-3 py-2 text-[13px] text-[#A1A1AA]">No templates yet.</div>
            )}
          </div>
        </div>

        {/* CENTER + RIGHT: editor */}
        {draft ? (
          <div className="flex-1 min-h-0 flex flex-col border border-[#E4E4E7] rounded-[12px] overflow-hidden">
            {/* Toolbar */}
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#E4E4E7]">
              <div className="flex items-center gap-2">
                <div className="flex bg-[#F4F4F5] rounded-[8px] p-0.5">
                  {[["cover", "Cover Page"], ["body", "Body Pages"]].map(([k, label]) => (
                    <button
                      key={k}
                      onClick={() => { setTab(k); setSelectedBlock(null); }}
                      className={`px-3 py-1.5 rounded-[6px] text-[13px] font-medium transition-colors ${tab === k ? "bg-white text-[#BB42AE] shadow-sm" : "text-[#71717A]"}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              {/* Auto-save status — changes persist automatically */}
              <div className="flex items-center gap-1.5 text-[13px] text-[#A1A1AA]">
                {saving ? (
                  <>
                    <span className="w-3.5 h-3.5 border-2 border-[#E4E4E7] border-t-[#BB42AE] rounded-full animate-spin" />
                    Saving…
                  </>
                ) : dirty ? (
                  "Unsaved changes…"
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2DAD4E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    Saved
                  </>
                )}
              </div>
            </div>

            {/* Body: preview + controls */}
            <div className="flex-1 min-h-0 flex">
              <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar bg-[#F8F9FA] p-6 flex items-start justify-center">
                {loadingDraft ? (
                  <div className="w-7 h-7 border-4 border-[#E4E4E7] border-t-[#BB42AE] rounded-full animate-spin mt-10" />
                ) : (
                  <TemplatePreview
                    tab={tab}
                    config={draft}
                    onChange={onChange}
                    selectedBlock={selectedBlock}
                    onSelectBlock={setSelectedBlock}
                  />
                )}
              </div>

              <div className="w-[320px] shrink-0 border-l border-[#E4E4E7] overflow-y-auto custom-scrollbar px-4 pb-6">
                <ConfigControls
                  tab={tab}
                  config={draft}
                  onChange={onChange}
                  selectedBlock={selectedBlock}
                  onSelectBlock={setSelectedBlock}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center border border-[#E4E4E7] rounded-[12px] text-[14px] text-[#A1A1AA]">
            Select a template, or create one to get started.
          </div>
        )}
      </div>

      {confirmDelete && (
        <DeleteTemplateModal
          name={confirmDelete.name}
          deleting={deleting}
          onClose={() => setConfirmDelete(null)}
          onConfirm={handleDelete}
        />
      )}

      {showCreate && (
        <CreateModal onClose={() => setShowCreate(false)} onCreate={handleCreate} saving={creating} />
      )}
    </div>
  );
};

export default ProposalTemplates;
